import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ExternalLink, AlertOctagon } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

interface BillingRow {
  id: string
  amount: number
  currency: string
  reason: string | null
  status: string
  created: number
  customer_email: string | null
  charge_id: string | null
}

interface BillingResponse {
  rows: BillingRow[]
  has_more: boolean
}

async function fetchDisputesOrRefunds(action: 'list_disputes' | 'list_refunds'): Promise<BillingResponse> {
  const { data, error } = await supabase.functions.invoke('admin-billing', {
    body: { action, limit: 100 },
  })
  if (error) throw new Error(String(error))
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error)
  return data as BillingResponse
}

function formatAmount(cents: number, currency: string): string {
  const value = cents / 100
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(value)
  } catch {
    return `${value} ${currency.toUpperCase()}`
  }
}

function statusColor(status: string): string {
  if (['won', 'succeeded'].includes(status)) return 'border-green-300 bg-green-50 text-green-800'
  if (['lost', 'failed'].includes(status)) return 'border-red-300 bg-red-50 text-red-800'
  if (['warning_needs_response', 'needs_response', 'pending'].includes(status))
    return 'border-yellow-300 bg-yellow-50 text-yellow-900'
  return ''
}

function Table({ rows, kind }: { rows: BillingRow[]; kind: 'dispute' | 'refund' }) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <AlertOctagon className="mb-4 h-12 w-12" />
        <p>No {kind === 'dispute' ? 'disputes' : 'refunds'} found.</p>
      </div>
    )
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="px-4 py-2 text-left">Created</th>
              <th className="px-4 py-2 text-left">Customer</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-left">Reason</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="px-4 py-2 text-xs text-muted-foreground">
                  {format(new Date(r.created * 1000), 'MMM d, yyyy')}
                </td>
                <td className="px-4 py-2">{r.customer_email ?? '—'}</td>
                <td className="px-4 py-2 text-right font-medium">
                  {formatAmount(r.amount, r.currency)}
                </td>
                <td className="px-4 py-2 text-xs">{r.reason ?? '—'}</td>
                <td className="px-4 py-2">
                  <Badge variant="outline" className={statusColor(r.status)}>
                    {r.status}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-right">
                  <a
                    href={`https://dashboard.stripe.com/${kind === 'dispute' ? 'disputes' : 'refunds'}/${r.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

export function DisputesPage() {
  const [tab, setTab] = useState<'disputes' | 'refunds'>('disputes')

  const disputesQ = useQuery({
    queryKey: ['admin-billing-disputes'],
    queryFn: () => fetchDisputesOrRefunds('list_disputes'),
    enabled: tab === 'disputes',
    staleTime: 60 * 1000,
  })
  const refundsQ = useQuery({
    queryKey: ['admin-billing-refunds'],
    queryFn: () => fetchDisputesOrRefunds('list_refunds'),
    enabled: tab === 'refunds',
    staleTime: 60 * 1000,
  })

  return (
    <div>
      <PageHeader
        title="Disputes & Refunds"
        description="Read-only Stripe billing events"
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'disputes' | 'refunds')}>
        <TabsList className="mb-4">
          <TabsTrigger value="disputes">Disputes</TabsTrigger>
          <TabsTrigger value="refunds">Refunds</TabsTrigger>
        </TabsList>

        <TabsContent value="disputes">
          {disputesQ.isLoading ? (
            <Skeleton className="h-64" />
          ) : disputesQ.error ? (
            <p className="text-sm text-destructive">
              {disputesQ.error instanceof Error ? disputesQ.error.message : 'Failed to load disputes'}
            </p>
          ) : (
            <Table rows={disputesQ.data?.rows ?? []} kind="dispute" />
          )}
        </TabsContent>

        <TabsContent value="refunds">
          {refundsQ.isLoading ? (
            <Skeleton className="h-64" />
          ) : refundsQ.error ? (
            <p className="text-sm text-destructive">
              {refundsQ.error instanceof Error ? refundsQ.error.message : 'Failed to load refunds'}
            </p>
          ) : (
            <Table rows={refundsQ.data?.rows ?? []} kind="refund" />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
